import { useEffect } from "react";

const SITE_NAME = "Wald Enterprise";
const SITE_URL = "https://wald.co.za";

function setMeta(attr, name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}='${name}']`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel='${rel}']`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-jsonld='${id}']`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function clearJsonLd(prefix) {
  document.head
    .querySelectorAll(`script[data-jsonld^='${prefix}']`)
    .forEach((el) => el.remove());
}

function setJsonLdAll(basePath, jsonLd) {
  const prefix = basePath || "page";
  clearJsonLd(prefix);
  const items = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);
  items.forEach((data, i) => {
    const type = Array.isArray(data["@type"]) ? data["@type"][0] : data["@type"];
    setJsonLd(`${prefix}::${type || i}`, data);
  });
}

export function useSeo({ title, description, path, image, jsonLd, noindex }) {
  useEffect(() => {
    const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
    document.title = fullTitle;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, follow" : "index, follow");
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:url", `${SITE_URL}${path || ""}`);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("name", "twitter:image", image);
    }
    setLink("canonical", `${SITE_URL}${path || ""}`);
    if (jsonLd) setJsonLdAll(path || "page", jsonLd);
  }, [title, description, path, image, jsonLd, noindex]);
}

export function faqJsonLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Wald Enterprise",
  alternateName: "Wald",
  url: SITE_URL,
  logo: `${SITE_URL}/brand/wald-mark-light.webp`,
  image: `${SITE_URL}/brand/wald-mark-light.webp`,
  description:
    "Wald is a specialist agricultural software development and AgriTech partner in South Africa — building AI solutions, farm and agricultural ERP systems, business intelligence, digital transformation and platform engineering for high-throughput, regulated agricultural operations.",
  areaServed: "ZA",
  email: "info@wald.co.za",
  telephone: "+27713360548",
  knowsAbout: [
    "Agricultural software development",
    "AgriTech",
    "Farm management software",
    "Agriculture AI solutions",
    "Precision agriculture software",
    "Agricultural automation",
    "Agricultural systems integration",
    "Digital transformation for agriculture",
    "Legacy system modernization",
    "Custom agricultural software",
    "Business intelligence for agriculture",
    "Agricultural ERP",
    "Agricultural data analytics",
    "Agricultural cloud solutions",
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Pretoria",
    addressCountry: "ZA",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "info@wald.co.za",
      telephone: "+27713360548",
      areaServed: "ZA",
      availableLanguage: ["en"],
    },
  ],
  sameAs: [],
};
